import { useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABEL,
  TASK_STATUSES,
  TASK_STATUS_LABEL,
  type TaskPriority,
  type TaskStatus,
  type TaskView,
} from '../../types/task'

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())
}

export interface TaskFormValues {
  caseId: string | null
  title: string
  description: string | null
  priority: TaskPriority
  assigneeUserId: string | null
  dueDate: string | null
}

export interface TaskFormModalProps {
  /** `undefined` = create; a `TaskView` = edit. */
  task?: TaskView | undefined
  /** Locks the Case field to this id (the Case "Tasks" tab already knows its own case). */
  fixedCaseId?: string | undefined
  /** Show a free-text Case-id field on create (My Work's standalone create — no case picker exists
   * anywhere in the product yet, the same disclosed "plain UUID field" gap `TeamPanel` already
   * names for its User-id field). */
  allowCaseIdEntry?: boolean
  onClose: () => void
  onSave: (values: TaskFormValues) => Promise<void>
  /** Present only in edit mode — a distinct action from Save, calling the real transitions endpoint. */
  onTransitionStatus?: ((toStatus: TaskStatus) => Promise<void>) | undefined
  saveError?: string | null
}

/**
 * The single create/edit form behind both "My Work" and the Case "Tasks" tab (design-system.md
 * §3.1 modal contract). Editing the mutable field set (title/description/priority/assignee/due) is
 * one action (`onSave`, `PATCH /tasks/{id}`); moving `status` is a SEPARATE, explicit action
 * (`onTransitionStatus`, `POST /tasks/{id}/transitions`) — mirrors the backend's own "status only
 * ever changes via an explicit sub-resource" posture (module.task README).
 */
export function TaskFormModal({
  task,
  fixedCaseId,
  allowCaseIdEntry = false,
  onClose,
  onSave,
  onTransitionStatus,
  saveError,
}: TaskFormModalProps) {
  const isEdit = Boolean(task)
  const [caseId, setCaseId] = useState(fixedCaseId ?? task?.caseId ?? '')
  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? 'medium')
  const [assigneeUserId, setAssigneeUserId] = useState(task?.assigneeUserId ?? '')
  const [dueDate, setDueDate] = useState(task?.dueDate ?? '')
  const [statusBusy, setStatusBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  // A fixed/locked caseId (the Case "Tasks" tab, or an edit's existing link) is never re-validated
  // as free-text input — the field is not even rendered in that case.
  const caseIdValid = Boolean(fixedCaseId) || caseId.trim() === '' || isUuidLike(caseId)
  const assigneeValid = assigneeUserId.trim() === '' || isUuidLike(assigneeUserId)
  const canSubmit = title.trim().length > 0 && caseIdValid && assigneeValid && !saving

  async function handleSubmit() {
    if (!canSubmit) return
    setSaving(true)
    try {
      await onSave({
        caseId: fixedCaseId ?? (caseId.trim() === '' ? null : caseId.trim()),
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
        priority,
        assigneeUserId: assigneeUserId.trim() === '' ? null : assigneeUserId.trim(),
        dueDate: dueDate.trim() === '' ? null : dueDate.trim(),
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleTransition(toStatus: TaskStatus) {
    if (!onTransitionStatus || !task || toStatus === task.status || statusBusy) return
    setStatusBusy(true)
    try {
      await onTransitionStatus(toStatus)
    } finally {
      setStatusBusy(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="task-form-title"
      title={isEdit ? 'Edit task' : 'New task'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {saveError ? <p className="iw-field-error">{saveError}</p> : null}

        {isEdit ? (
          <div className="iw-field">
            <span className="iw-field-label">Status</span>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
              {TASK_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="iw-chip"
                  aria-pressed={task!.status === s}
                  disabled={statusBusy || task!.status === s}
                  onClick={() => void handleTransition(s)}
                >
                  {TASK_STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {allowCaseIdEntry && !fixedCaseId ? (
          <div className="iw-field">
            <label className="iw-field-label" htmlFor="task-case-id">
              Case ID (optional)
            </label>
            <input
              id="task-case-id"
              className="iw-input"
              aria-invalid={!caseIdValid}
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            {!caseIdValid ? <span className="iw-field-error">Not a valid case id.</span> : null}
          </div>
        ) : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="task-title">
            Title
          </label>
          <input
            id="task-title"
            className="iw-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="task-description">
            Description
          </label>
          <textarea
            id="task-description"
            className="iw-input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--iw-space-3)' }}>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="task-priority">
              Priority
            </label>
            <select
              id="task-priority"
              className="iw-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
            >
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {TASK_PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="task-due-date">
              Due date
            </label>
            <input
              id="task-due-date"
              type="date"
              className="iw-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="task-assignee">
            Assignee user ID
          </label>
          <input
            id="task-assignee"
            className="iw-input"
            aria-invalid={!assigneeValid}
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
          {!assigneeValid ? <span className="iw-field-error">Not a valid user id.</span> : null}
        </div>
      </form>
    </FocusTrapDialog>
  )
}
